// ============================================================
// REVENUE & PAYOUT PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { DollarSign, Download } from 'lucide-react';
import { Table, Badge, Button, SearchInput, Pagination, StatCard } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getCommissionData } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency, formatDate, downloadCSV } from '../utils/helpers';

const RevenuePayout = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.getCommissionData(getCommissionData)
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || []);
        setAll(arr.map(d => ({ ...d, restaurant: d.restaurant || d.restaurantName || '', orders: d.orders || d.totalOrders || 0, platformFee: d.platformFee || 0 })));
        setLoading(false);
      })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(all, ['restaurant', 'period']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(filtered);

  const totalRevenue = all.reduce((s, d) => s + d.grossRevenue, 0);
  const totalCommission = all.reduce((s, d) => s + d.commissionAmount, 0);
  const totalPayout = all.reduce((s, d) => s + d.netPayout, 0);

  const columns = [
    { key: 'restaurant', label: 'Restaurant', render: v => <b>{v}</b> },
    { key: 'period', label: 'Period' },
    { key: 'orders', label: 'Orders' },
    { key: 'grossRevenue', label: 'Gross Revenue', render: v => <b>{formatCurrency(v)}</b> },
    { key: 'commissionRate', label: 'Comm %', render: v => `${v}%` },
    { key: 'commissionAmount', label: 'Commission', render: v => <span style={{ color: '#10B981', fontWeight: 700 }}>{formatCurrency(v)}</span> },
    { key: 'platformFee', label: 'Platform Fee', render: v => formatCurrency(v) },
    { key: 'netPayout', label: 'Net Payout', render: v => <b style={{ color: '#3B82F6' }}>{formatCurrency(v)}</b> },
    {
      key: 'status', label: 'Status',
      render: v => <span className="admin-badge" style={{ color: v === 'paid' ? '#10B981' : '#F59E0B', background: v === 'paid' ? '#D1FAE5' : '#FEF3C7' }}>{v}</span>,
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Revenue & Payout Reports</h1>
          <p className="admin-page-subtitle">Platform revenue and restaurant payout management</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'revenue')}>Export</Button>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <StatCard label="Gross Revenue" value={formatCurrency(totalRevenue)} icon={<DollarSign size={18} />} color="#E85D04" />
        <StatCard label="Commission Earned" value={formatCurrency(totalCommission)} icon={<DollarSign size={18} />} color="#10B981" />
        <StatCard label="Net Payouts" value={formatCurrency(totalPayout)} icon={<DollarSign size={18} />} color="#3B82F6" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by restaurant..." />
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={10} onPageChange={goTo} />
      </div>
    </div>
  );
};

export default RevenuePayout;
